<template>
    <div class="content pt-0 invoice-detail-page">
        <div class="container invoice-detail-container">
            <div v-if="loading" class="text-center py-5">
                <i class="fa fa-spin fa-spinner fa-2x text-muted"></i>
                <p class="mt-2 text-muted">{{ t('invoices-loading_details') }}</p>
            </div>

            <div v-else-if="error" class="text-center py-5">
                <i class="fa ui-alert-circle text-danger error-icon"></i>
                <h3 class="mt-3">{{ t('invoices-error_loading') }}</h3>
                <p class="text-muted">{{ error }}</p>
                <kv-button variant="outlined" class="mt-3" @click="goBack">{{ t('invoices-details_back') }}</kv-button>
            </div>

            <div v-else-if="invoice" class="invoice-detail-content">
                <invoice-detail-header 
                    :invoice="invoice" 
                    :isAdmin="isAdmin"
                    :editMode="editMode"
                    :editSaving="editSaving"
                    @back="goBack"
                    @status-changed="onStatusChanged"
                    @start-edit="startEdit"
                    @save-edit="saveEdit"
                    @cancel-edit="cancelEdit"
                    @header-change="onHeaderChange"
                    @scroll-to-bank-slip="scrollToBankSlip"
                    @export-csv="handleExportCsv"
                    @open-payment-modal="openPaymentModal"
                />

                <div class="row">
                    <div class="col-12">
                        <invoice-cost-breakdown 
                            ref="breakdown"
                            :invoice="invoice"
                            :editMode="editMode && invoice.status !== 'pending' && invoice.status !== 'paid'"
                            :payment-edit="editMode && invoice.status === 'paid'"
                            :is-admin="isAdmin"
                            @change="onBreakdownChange"
                        />
                    </div>
                </div>

                <div class="row mt-2" v-if="!isAdmin && invoice.payment_link_url && invoice.status !== 'paid' && invoice.status !== 'cancelled'">
                    <div class="col-12">
                        <KvCard class="payment-link-card">
                            <template #text>
                                <div class="d-flex flex-row align-items-center justify-content-between">
                                    <div class="d-flex align-items-center">
                                        <div class="payment-method-icon-box mr-3 d-none d-sm-flex align-items-center justify-content-center online-bg">
                                            <Icon icon-class="ui-credit-card-refresh" size="4" color="#2aaa4c" />
                                        </div>
                                        <div>
                                            <p class="mb-0 font-weight-500 text-dark">{{ t('invoices-online_payment_available') }}</p>
                                            <p class="mb-0 text-muted small">{{ t('invoices-click_to_pay_online') }}</p>
                                        </div>
                                    </div>
                                    <kv-button
                                        variant="filled"
                                        prepend-icon="ui-credit-card-refresh"
                                        @click="openPaymentLink"
                                    >
                                        {{ t('invoices-pay_online_btn') }}
                                    </kv-button>
                                </div>
                            </template>
                        </KvCard>
                    </div>
                </div>

                <div class="row mt-2" v-if="!isAdmin && invoice.bank_slip_url">
                    <div class="col-12">
                        <KvCard class="bank-slip-card">
                            <template #text>
                                <div class="d-flex flex-row align-items-center justify-content-between">
                                    <div class="d-flex align-items-center">
                                        <div class="payment-method-icon-box mr-3 d-none d-sm-flex align-items-center justify-content-center slip-bg">
                                            <Icon icon-class="ui-file-02" size="4" color="#4B5563" />
                                        </div>
                                        <div>
                                            <p class="mb-0 font-weight-500 text-dark">{{ t('invoices-bank_slip_available') }}</p>
                                            <p class="mb-0 text-muted small">{{ t('invoices-click_to_open_bank_slip') }}</p>
                                        </div>
                                    </div>
                                    <kv-button
                                        variant="outlined"
                                        prepend-icon="ui-file-02"
                                        @click="openBankSlip"
                                    >
                                        {{ t('invoices-view_bank_slip') }}
                                    </kv-button>
                                </div>
                            </template>
                        </KvCard>
                    </div>
                </div>

                <div id="admin-tools-row" class="row mt-2" v-if="isAdmin">
                    <div class="col-lg-6 mb-4">
                        <invoice-attach-bank-slip
                            id="attach-bank-slip-section"
                            :invoice="invoice"
                            :invoice-id="invoiceId"
                            @uploaded="onBankSlipUploaded"
                            @refresh="fetchInvoice"
                        />
                    </div>
                    <div class="col-lg-6 mb-4">
                        <invoice-payment-link
                            id="payment-link-section"
                            :invoice="invoice"
                            @updated="onPaymentLinkUpdated"
                            @refresh="fetchInvoice"
                        />
                    </div>
                    <div class="col-12">
                        <invoice-internal-notes
                            :invoice="invoice"
                            @updated="updateInternalNotes"
                        />
                    </div>
                </div>

                <div v-if="editMode" class="invoice-edit-footer">
                    <kv-button variant="outlined" prepend-icon="ui-x-close" @click="cancelEdit" :disabled="editSaving">
                        {{ t('invoices-cancel') }}
                    </kv-button>
                    <kv-button variant="filled" :prepend-icon="editSaving ? 'ui-loading-01' : 'ui-check'" @click="saveEdit" :disabled="editSaving">
                        {{ t('invoices-save_changes') }}
                    </kv-button>
                </div>
            </div>
        </div>

        <invoice-payment-method-modal
            ref="paymentMethodModal"
            :invoice="invoice"
        />
    </div>
</template>

<script>
import keviApi from '../keviApi';
import { extractApiError } from './helpers';
import InvoiceDetailHeader from './components/invoice-detail-header.vue';
import InvoiceCostBreakdown from './components/invoice-cost-breakdown.vue';
import InvoiceInternalNotes from './components/invoice-internal-notes.vue';
import InvoiceAttachBankSlip from './components/invoice-attach-bank-slip.vue';
import InvoicePaymentLink from './components/invoice-payment-link.vue';
import InvoicePaymentMethodModal from './components/invoice-payment-method-modal.vue';
import KvCard from '../components/ui/kv-card.vue';
import Icon from '../components/icon/icon.vue';

export default {
    name: "invoice-detail",
    components: {
        InvoiceDetailHeader,
        KvCard,
        Icon,
        InvoiceCostBreakdown,
        InvoiceInternalNotes,
        InvoiceAttachBankSlip,
        InvoicePaymentLink,
        InvoicePaymentMethodModal
    },
    data() {
        return {
            loading: true,
            error: null,
            invoice: null,
            editMode: false,
            editSaving: false,
            editedFields: {}
        }
    },
    computed: {
        invoiceId() {
            return this.$route.params.id;
        },
        isAdmin() {
            return !!this.$store?.state?.user?.global_admin;
        }
    },
    mounted() {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    },
    created() {
        const permissions = this.$store.state.user?.permissions || {};
        const hasPermission = permissions.invoices?.enabled === 1;

        if (!hasPermission) {
            this.$router.replace('/');
            return;
        }
        this.fetchInvoice();
    },
    methods: {
        async fetchInvoice() {
            if (!this.invoiceId) return;
            
            this.loading = true;
            this.error = null;
            
            try {
                const endpoint = this.isAdmin 
                    ? `invoices/${this.invoiceId}/admin` 
                    : `invoices/${this.invoiceId}`;
                    
                const options = keviApi.getOptions(endpoint, 'GET');
                const response = await this.$http(options);
                
                if (response.status === 200 && response.body && response.body.data) {
                    this.invoice = response.body.data;
                } else {
                    this.error = this.t('invoices-load_error_not_found');
                }
            } catch (error) {
                console.error("Error fetching invoice:", error);
                this.error = this.t('invoices-load_error_general');
            } finally {
                this.loading = false;
                await this.$nextTick();
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            }
        },

        startEdit() {
            this.editedFields = {};
            this.editMode = true;
        },

        cancelEdit() {
            this.editMode = false;
            this.editedFields = {};
        },

        async saveEdit() {
            this.editSaving = true;
            try {
                const finalPayload = {};
                for (const key in this.editedFields) {
                    const newValue = this.editedFields[key];
                    const oldValue = this.invoice[key];
                    if (['created_at', 'due_date', 'billing_cycle_start', 'billing_cycle_end'].includes(key)) {
                        const newDateStr = String(newValue || '').substring(0, 10);
                        const oldDateStr = String(oldValue || '').substring(0, 10);
                        if (newDateStr === oldDateStr) continue;
                    }

                    if (typeof newValue === 'object' && newValue !== null) {
                        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                            finalPayload[key] = newValue;
                        }
                    } else if (newValue !== oldValue) {
                        finalPayload[key] = newValue;
                    }
                }

                if (Object.keys(finalPayload).length === 0) {
                    this.$store.dispatch('notification/info', this.t('invoices-no_changes_to_save'));
                    this.editMode = false;
                    return;
                }

                const options = keviApi.getOptions(
                    `invoices/${this.invoice.id}/fields`,
                    'PATCH',
                    finalPayload
                );
                const response = await this.$http(options);

                if (response.status === 200) {
                    this.$store.dispatch('notification/success', this.t('invoices-update_success'));
                    this.editMode = false;
                    this.editedFields = {};
                    await this.fetchInvoice();
                } else {
                    const msg = extractApiError(response, this.t('invoices-update_error'));
                    this.$store.dispatch('notification/error', msg);
                }
            } catch (e) {
                const msg = extractApiError(e, this.t('invoices-update_connection_error'));
                this.$store.dispatch('notification/error', msg);
            } finally {
                this.editSaving = false;
            }
        },

        onBreakdownChange(fields) {
            this.editedFields = { ...this.editedFields, ...fields };
        },

        onHeaderChange(fields) {
            this.editedFields = { ...this.editedFields, ...fields };
        },

        updateInternalNotes(notes) {
            if (this.invoice) {
                this.invoice.internal_notes = notes;
            }
        },
        onBankSlipUploaded(url) {
            if (this.invoice) {
                this.invoice.bank_slip_url = url;
            }
        },
        onPaymentLinkUpdated(url) {
            if (this.invoice) {
                this.invoice.payment_link_url = url;
            }
        },
        scrollToBankSlip() {
            const el = document.getElementById('attach-bank-slip-section');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        onStatusChanged(newStatus) {
            if (this.invoice) {
                this.invoice.status = newStatus;
                if (newStatus === 'pending') {
                    this.goBack();
                }
            }
        },
        goBack() {
            const fallback = this.isAdmin ? '/invoices' : '/invoices/my-invoice';
            if (window.history.length > 2) {
                this.$router.go(-1);
            } else {
                this.$router.push(fallback);
            }
        },
        openBankSlip() {
            if (this.invoice && this.invoice.bank_slip_url) {
                window.open(this.invoice.bank_slip_url, '_blank');
            }
        },
        openPaymentLink() {
            if (this.invoice && this.invoice.payment_link_url) {
                window.open(this.invoice.payment_link_url, '_blank');
            }
        },
        openPaymentModal() {
            if (this.$refs.paymentMethodModal) {
                this.$refs.paymentMethodModal.open();
            }
        },
        handleExportCsv() {
            if (this.$refs.breakdown) {
                this.$refs.breakdown.exportCsv();
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.invoice-detail-page {
    padding-top: 24px;
    padding-bottom: 40px;
    background-color: #F9FAFB;
    min-height: 100vh;
}

.invoice-detail-container {
    max-width: 1200px !important;
    margin: 0 auto !important;
}

.error-icon {
    font-size: 48px;
}

.bank-slip-card, .payment-link-card {
    ::v-deep .kv-card-content-inner {
        padding: 16px !important;
    }
}

.payment-method-icon-box {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    flex-shrink: 0;

    &.online-bg {
        background-color: #ECFDF5;
    }

    &.slip-bg {
        background-color: #F3F4F6;
    }
}

.invoice-edit-footer {
    position: sticky;
    bottom: 16px;
    z-index: 20;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.08);
}
</style>
